import {
  ScheduledTransactionDetails,
  ScheduledTransactionStatus,
} from '@/types/transactions';
import { SVSummaryCard } from '../core';
import { SVSummaryCardItem } from '@/types/common';
import { formatCurrency } from '@/utils/currency';
import { titleCase } from '@/utils/stringCaser';
import { Box, Chip, Typography } from '@mui/material';
import dayjs from 'dayjs';
import BankLogo from '../icons/BankLogo';
import {
  SCHEDULED_TRANSACTION_SEND_ON_MONTHLY_OPTIONS,
  SCHEDULED_TRANSACTION_SEND_ON_WEEKLY_OPTIONS,
} from '@/constants/scheduledTransactions';
import OneAccountIcon from '../icons/OneAccountIcon';

type ScheduledfTransactionDetailsCardProps = {
  scheduledTransactionDetails: ScheduledTransactionDetails;
};

export default function ScheduledfTransactionDetailsCard({
  scheduledTransactionDetails,
}: ScheduledfTransactionDetailsCardProps) {
  const {
    status,
    displayType,
    amount,
    recurringScheduleType,
    scheduleDayOfMonth,
    scheduleDayOfWeek,
    scheduleStopSendingAfter,
    scheduleStopSendingAfterNumberOfOccurrences,
    numberOfRemainingOccurrences,
    scheduleType,
    nextOccurrenceDate,
    note,
    instructions,
    fromAccount,
    fromAccountDetails,
    toAccount,
    toAccountDetails,
    description,
  } = scheduledTransactionDetails;
  const isPaused = status === ScheduledTransactionStatus.PAUSED;
  const isMonthly =
    recurringScheduleType === 'monthly' ||
    recurringScheduleType === 'every other month';

  const baseItems: SVSummaryCardItem[] = [
    {
      label: 'Status',
      customContent: (
        <Chip
          color={
            status === ScheduledTransactionStatus.CANCELED
              ? 'error'
              : status === ScheduledTransactionStatus.ACTIVE
                ? 'success'
                : 'default'
          }
          size="small"
          label={titleCase(status)}
        />
      ),
    },
    {
      label: 'Type',
      customContent: <Chip size="small" label={displayType} />,
    },
    {
      label: 'From',
      customContent: (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 1,
            alignItems: 'center',
          }}>
          {fromAccountDetails.bankLogoUrl && (
            <BankLogo src={fromAccountDetails.bankLogoUrl} />
          )}
          {fromAccountDetails.bankAccountOrigin === 'internal' && (
            <OneAccountIcon color="primary" />
          )}
          <Typography>{fromAccount}</Typography>
        </Box>
      ),
    },
    {
      label: 'To',
      customContent: (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 1,
            alignItems: 'center',
          }}>
          {toAccountDetails.bankLogoUrl && (
            <BankLogo src={toAccountDetails.bankLogoUrl} />
          )}
          {toAccountDetails.bankAccountOrigin === 'internal' && (
            <OneAccountIcon color="primary" />
          )}
          <Typography>{toAccount}</Typography>
        </Box>
      ),
    },
    {
      label: 'Amount',
      text: formatCurrency({ amount: amount.value }),
    },
    {
      label: 'Description',
      text: description,
    },
    {
      label: 'Next Occurence',
      text: isPaused
        ? 'Paused'
        : dayjs(nextOccurrenceDate).format('MMMM DD, YYYY'),
    },
  ];
  const notesAndInstructions: SVSummaryCardItem[] = [
    {
      label: 'Note',
      text: note || '',
    },
    {
      label: 'Instructions',
      text: instructions || '',
    },
  ];

  let items: SVSummaryCardItem[] = [];
  if (scheduleType === 'one time') {
    items = [
      ...baseItems,
      {
        label: 'Frequency',
        text: 'One Time',
      },
      ...notesAndInstructions,
    ];
  } else {
    items = [
      ...baseItems,
      {
        label: 'Frequency',
        text: titleCase(recurringScheduleType),
      },
      {
        label: 'Send On',
        text: isMonthly
          ? SCHEDULED_TRANSACTION_SEND_ON_MONTHLY_OPTIONS.find(
              o => o.value === `${scheduleDayOfMonth}`,
            )?.label || ''
          : SCHEDULED_TRANSACTION_SEND_ON_WEEKLY_OPTIONS.find(
              o => o.value === scheduleDayOfWeek,
            )?.label || '',
      },
      {
        label: 'Stop Sending After',
        text: `${scheduleStopSendingAfter === 'do not stop' ? 'Never' : scheduleStopSendingAfterNumberOfOccurrences}`,
      },
      {
        label: 'Remaining Transfers',
        text: numberOfRemainingOccurrences
          ? `${numberOfRemainingOccurrences}`
          : '',
      },
      ...notesAndInstructions,
    ];
  }

  return <SVSummaryCard label="Basic Info" items={items} />;
}
